import type { SelectChangeEvent } from '@mui/material';
import {
  Checkbox, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput,
  Paper, Select,   Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow,   TextField } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { formatToText, Months } from '../../../../common-utils/formatters';
import classNames from '../../../utils/classNames';

export type MUITableRow = Record<string, React.ReactNode>;

export type MUITableHeader = {
  title: string;
  sortField?: string;
  sortable?: boolean;
  onSort?: (field: string, direction?: 'asc' | 'desc') => void;
  render?: () => React.ReactNode;
  class?: string;
  customContentClass?: string;
  customContentSelectedClass?: string;
};

interface MUITableProps {
  data: MUITableRow[];
  headers: MUITableHeader[];
  tableClass?: string;
  dataChangeCallback?: (checked: boolean, index: number, item: MUITableRow) => void;
  noPaddingColumns?: string[];
  filterColumnName?: string;
  selectedFilteredValues?: string[];
  isSearchable?: boolean;
  setParentFilteredData?: (data: MUITableRow[]) => void;
  searchClassName?: string;
  sortableColumns?: string[];
  disablePagination?: boolean;
  customRowClass?: (row: MUITableRow) => string;
  // API Pagination props
  apiPagination?: boolean;
  totalCount?: number;
  onPageChange?: (page: number, rowsPerPage: number) => void;
  currentPage?: number;
  currentRowsPerPage?: number;
  // API Sorting props
  currentSortField?: string;
  currentSortDirection?: 'asc' | 'desc';
  // Client Sorting defaults
  defaultSortKey?: string;
  defaultSortDirection?: 'asc' | 'desc';
  headerTopRenderer?: (args: { headers: MUITableHeader[]; hasSelection: boolean }) => Array<{ content: React.ReactNode; colSpan?: number; className?: string }>;
}

const MUITable = ({
  data,
  headers,
  tableClass = '',
  noPaddingColumns = [],
  filterColumnName,
  selectedFilteredValues = [],
  isSearchable = false,
  setParentFilteredData = null,
  searchClassName = null,
  sortableColumns = [],
  disablePagination = false,
  customRowClass,
  dataChangeCallback,
  // API Pagination props
  apiPagination = false,
  totalCount,
  onPageChange,
  currentPage = 0,
  currentRowsPerPage = 10,
  // API Sorting props
  currentSortField = '',
  currentSortDirection = 'asc',
  // Client Sorting defaults
  defaultSortKey,
  defaultSortDirection = 'asc',
  // Optional
  headerTopRenderer,
}: MUITableProps) => {
  const [page, setPage] = useState(currentPage);
  const [rowsPerPage, setRowsPerPage] = useState(currentRowsPerPage);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterValues, setFilterValues] = useState<string[]>(selectedFilteredValues);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const prevFilteredLenRef = useRef<number>(0);

  // Update internal state when controlled props change
  useEffect(() => {
    if (apiPagination) {
      // Debug: controlled pagination updates
      setPage(currentPage);
      setRowsPerPage(currentRowsPerPage);
    }
  }, [apiPagination, currentPage, currentRowsPerPage]);

  // Update sort indicator when the API sort changes
  useEffect(() => {
    if (currentSortField && apiPagination) {
      setSortConfig({ key: currentSortField, direction: currentSortDirection });
    }
  }, [currentSortField, currentSortDirection, apiPagination]);

  // Initialize default client-side sort
  useEffect(() => {
    if (!apiPagination && defaultSortKey) {
      setSortConfig({ key: defaultSortKey, direction: defaultSortDirection });
    }
  }, [apiPagination, defaultSortKey, defaultSortDirection]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, index: number, item: MUITableRow) => {
    if (dataChangeCallback) {
      dataChangeCallback(event.target.checked, index, item);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    if (apiPagination && onPageChange) {
      onPageChange(newPage, rowsPerPage);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value);
    if (apiPagination && onPageChange) {
      onPageChange(0, newRowsPerPage);
    }
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // reset pagination when searching to avoid confusion being on a page not even reachable anymore based on the search
    setPage(0);
    setSearchQuery(event.target.value);
  };

  const handleFilterValuesChange = (event: SelectChangeEvent<typeof filterValues>) => {
    const {
      target: { value },
    } = event;
    setFilterValues(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSort = (key: string) => {
    const header = headers.find((h: MUITableHeader) => h.title === key);

    const effectiveKey = (header && header.sortField) ? header.sortField : key;

    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === effectiveKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: effectiveKey, direction });
    setPage(0); // Reset to first page when sorting
    
    const apiHeader = headers.find((h: MUITableHeader) => h.title === key || h.sortField === key);
    if (apiHeader && apiHeader.onSort && apiPagination) {
      apiHeader.onSort(apiHeader.sortField || key, direction);
    }
  };

  const isNumeric = (str: string): boolean => !isNaN(parseFloat(str)) && isFinite(Number(str));

  const isValidDate = (str: string): boolean => {
    const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!datePattern.test(str)) {return false;}
    const [day, month, year] = str.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  };

  const isValidISODate = (str: string): boolean => {
    const isoPattern = /^\d{4}-\d{2}-\d{2}$/;
    if (!isoPattern.test(str)) {return false;}
    const [year, month, day] = str.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  };

  // Parse French date format like "1 Avril 2026" or "10 Février 2026"
  const parseFrenchDate = (str: string): number | null => {
    const frenchDatePattern = /^(\d{1,2})\s+([A-Za-zÀ-ÿ]+)\s+(\d{4})$/;
    const match = str.match(frenchDatePattern);
    if (!match) {return null;}
    const [, dayStr, monthName, yearStr] = match;
    const day = Number(dayStr);
    const year = Number(yearStr);
    const monthIndex = Months.findIndex(m => m.toLowerCase() === monthName.toLowerCase());
    if (monthIndex === -1) {return null;}
    const date = new Date(year, monthIndex, day);
    if (date.getFullYear() === year && date.getMonth() === monthIndex && date.getDate() === day) {
      return date.getTime();
    }
    return null;
  };

  const parseValue = (value: string) => {
    const trimmed = value.trim();
    // Treat common placeholder markers as empty values
    if (trimmed === '-' || trimmed === '—' || trimmed === '–') {return '';} 
    
    // Try French date format FIRST (e.g., "1 Avril 2026") - before currency parsing which would corrupt it
    const frenchDateTs = parseFrenchDate(trimmed);
    if (frenchDateTs !== null) {return frenchDateTs;}
    // Same for DD/MM/YYYY, which the currency parsing below would read as the number DDMMYYYY
    if (isValidDate(trimmed)) {
      const [day, month, year] = trimmed.split('/').map(Number);
      return new Date(year, month - 1, day).getTime();
    }

    // Extract numeric value from currency-like strings (remove spaces, currency symbols, non-numeric chars except , . and -)
    const currencyLike = trimmed.replaceAll(/[^0-9,.-]/g, '').replaceAll(/\s+/g, '');
    if (currencyLike !== '' && /[0-9]/.test(currencyLike) && (currencyLike !== trimmed)) {
      const normalized = currencyLike.replace(',', '.');
      const parsed = parseFloat(normalized);
      if (!Number.isNaN(parsed)) {return parsed;}
    }
    if (isNumeric(trimmed)) {return parseFloat(trimmed);}
    if (isValidISODate(trimmed)) {
      const [year, month, day] = trimmed.split('-').map(Number);
      return new Date(year, month - 1, day).getTime();
    }
    return trimmed.toLowerCase();
  };

  const sortedData = useMemo(() => {
    if (!sortConfig || apiPagination) {return data;} // Skip sorting if using API pagination
    // Parse each cell once, not at every comparison: tables can hold tens of thousands of rows
    const keyed = data.map((row) => ({ row, value: parseValue(formatToText(row[sortConfig.key])) }));
    keyed.sort((a, b) => {
      const parsedAValue = a.value;
      const parsedBValue = b.value;

      // Handle empty values to sort them at the end
      const aIsEmpty = parsedAValue === '';
      const bIsEmpty = parsedBValue === '';
  
      if (aIsEmpty && !bIsEmpty) {return 1;}  // a is empty, b is not: a should be after b
      if (!aIsEmpty && bIsEmpty) {return -1;} // a is not empty, b is: a should be before b
      if (aIsEmpty && bIsEmpty) {return 0;}   // both are empty: they are equal
  
      // Regular comparison
      if (parsedAValue < parsedBValue) {return sortConfig.direction === 'asc' ? -1 : 1;}
      if (parsedAValue > parsedBValue) {return sortConfig.direction === 'asc' ? 1 : -1;}
      return 0;
    });
    return keyed.map(({ row }) => row);
  }, [data, sortConfig, apiPagination]);

  const filteredData = useMemo(() => {
    // Skip filtering if using API pagination
    if (apiPagination) {return data;}
    
    let filtered = sortedData;

    if (searchQuery) {
      filtered = filtered.filter(item =>
        headers.some(header =>
          formatToText(item[header.title])?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      );
    }

    if (filterColumnName && filterValues.length > 0) {
      filtered = filtered.filter(item =>
        filterValues.includes(formatToText(item[filterColumnName])),
      );
    }

    return filtered;
  }, [sortedData, searchQuery, filterColumnName, filterValues, headers, apiPagination, data]);

  // Keep the user on a valid page when data updates in place (e.g. saving a field, refreshing a
  // row). Only fall back to page 0 if the current page becomes unreachable (e.g. rows removed
  // by qualifying a DPE). Skipped for controlled API pagination, which manages pages externally.
  useEffect(() => {
    if (apiPagination) {return;}
    const maxPage = Math.max(0, Math.ceil(filteredData.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(0);
    }
  }, [filteredData.length, rowsPerPage, page, apiPagination]);

  const paginatedData = useMemo(() => {
    if (disablePagination || apiPagination) {
      return filteredData;
    }
    const startIndex = page * rowsPerPage;
    if (prevFilteredLenRef.current !== filteredData.length) {
      prevFilteredLenRef.current = filteredData.length;
    }
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage, disablePagination, apiPagination]);

  const filterOptions = useMemo(() => {
    if (!filterColumnName) {return [];}
    const options = new Set<string>();
    data.forEach(item => {
      if (formatToText(item[filterColumnName])) {
        options.add(formatToText(item[filterColumnName]));
      }
    });
    // Sort the options: dates in ascending order, other values alphabetically
    return [...options].sort((a, b) => {
      // Try parsing as dates (supports DD/MM/YYYY and French format like "1 Avril 2026")
      const aDateTs = parseFrenchDate(a);
      const bDateTs = parseFrenchDate(b);
      
      // Also check DD/MM/YYYY format
      const datePatternDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/;
      let aTs = aDateTs;
      let bTs = bDateTs;
      
      if (aTs === null && datePatternDDMMYYYY.test(a)) {
        const [dayA, monthA, yearA] = a.split('/').map(Number);
        aTs = new Date(yearA, monthA - 1, dayA).getTime();
      }
      if (bTs === null && datePatternDDMMYYYY.test(b)) {
        const [dayB, monthB, yearB] = b.split('/').map(Number);
        bTs = new Date(yearB, monthB - 1, dayB).getTime();
      }
      
      // If both are dates, sort chronologically ascending
      if (aTs !== null && bTs !== null) {
        return aTs - bTs;
      }
      
      // Dates come before non-dates
      if (aTs !== null && bTs === null) {return -1;}
      if (aTs === null && bTs !== null) {return 1;}
      
      // Non-dates: alphabetical order
      return a.localeCompare(b, 'fr');
    });
  }, [data, filterColumnName]);

  useEffect(() => {
    if (setParentFilteredData) {
      setParentFilteredData(filteredData);
    }
  }, [searchQuery, filterValues, filteredData, setParentFilteredData]);

  const renderRow = (item: MUITableRow, itemIndex: number) => {
    let columnIndex = 0;
    const cells = [];

    if (dataChangeCallback) {
      const baseIndex = (!apiPagination && !disablePagination) ? page * rowsPerPage : 0;
      const globalIndex = baseIndex + itemIndex;
      const isCheckboxDisabled = Boolean(item._checkboxDisabled);
      cells.push(
        <TableCell key={`checkbox-${itemIndex}`} padding="checkbox">
          <Checkbox
            checked={Boolean(item.VALIDE)}
            onChange={(event) => handleCheckboxChange(event, globalIndex, item)}
            disabled={isCheckboxDisabled}
          />
        </TableCell>,
      );
    }

    for (let i = 0; i < headers.length; i++) {
      const header = headers[i];
      const cellContent = item[header.title];

      if (columnIndex > i) {
        // Skip this iteration if we're still within a colspan
        continue;
      }

      let colSpan = 1;
      let cellToRender: React.ReactNode = cellContent;

      if (typeof cellContent === 'object' && cellContent !== null) {
        const obj = cellContent as { content?: React.ReactNode; props?: { colSpan?: number } };
        if ('content' in obj && 'props' in obj && obj.content != null) {
          colSpan = obj.props?.colSpan || 1;
          cellToRender = obj.content;
        } else if (obj.props && 'colSpan' in obj.props) {
          colSpan = obj.props.colSpan ?? 1;
        }
      }

      cells.push(
        <TableCell
          key={`${itemIndex}-${i}`}
          className={`border border-slate-500 ${
            noPaddingColumns && noPaddingColumns.includes(header.title) ? 'p-0' : 'px-1'
          } ${(header.customContentSelectedClass) ? header.customContentSelectedClass : ''}
          ${(header.customContentClass) ? header.customContentClass : ''}`}
          colSpan={colSpan}
        >
          {cellToRender}
        </TableCell>,
      );

      columnIndex += colSpan;
    }

    return cells;
  };

  return (
    <div>
      {(filterColumnName || isSearchable) && (
        <div className="mb-4 pt-2 md:flex md:justify-between">
          {isSearchable && (
            <TextField
              label="Rechercher"
              value={searchQuery}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              sx={{ '.MuiInputBase-input': { ':focus': { boxShadow: 'none' } } }}
              className='w-full md:w-auto'
            />
          )}
          {filterColumnName && (
            <FormControl variant="outlined" size="small" className={classNames(!searchClassName && 'w-full md:w-auto md:min-w-[250px] md:max-w-full', searchClassName)}>
              <InputLabel>{filterColumnName}</InputLabel>
              <Select
                multiple
                value={filterValues}
                onChange={handleFilterValuesChange}
                input={<OutlinedInput label="Filter Values" />}
                renderValue={(selected) => selected.join(', ')}
                className='w-full md:w-auto md:max-w-full md:min-w-[250px]'
              >
                {filterOptions.map((value, index) => (
                  <MenuItem key={index} value={value}>
                    <Checkbox checked={filterValues.indexOf(value) > -1} />
                    <ListItemText primary={value} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      )}
      <TableContainer component={Paper}>
        <Table className={classNames('border border-slate-500 w-full leading-[3rem]', tableClass)}>
          <TableHead className="bg-[#CCD4DE] text-center">
            {headerTopRenderer && (
              <TableRow>
                {dataChangeCallback && <TableCell padding="checkbox" />}
                {headerTopRenderer({ headers, hasSelection: Boolean(dataChangeCallback) }).map((cell, idx) => (
                  <TableCell
                    key={`top-${idx}`}
                    className={classNames('border-x border-slate-500 font-normal p-1', cell.className)}
                    colSpan={cell.colSpan || 1}
                  >
                    {cell.content}
                  </TableCell>
                ))}
              </TableRow>
            )}
            <TableRow>
              {dataChangeCallback && <TableCell padding="checkbox" />}
              {headers.map((header, index) => (
                <TableCell
                  className={classNames('border-x border-slate-500 font-normal p-1', header.class)}
                  key={index}
                  onClick={() => {
                    // Check if header is sortable either through sortable flag or sortField property
                    const isSortable = sortableColumns.includes(header.title) || header.sortable;
                    if (isSortable) {
                      if (header.sortField && header.onSort && apiPagination) {
                        // For API-based sorting
                        header.onSort(header.sortField);
                      } else {
                        // For client-side sorting
                        handleSort(header.title);
                      }
                    }
                  }}
                  style={{ 
                    cursor: (sortableColumns.includes(header.title) || header.sortable) ? 'pointer' : 'default', 
                  }}
                >
                  {header.render ? header.render() : header.title}
                  {(sortableColumns.includes(header.title) || header.sortable) && (
                    ((sortConfig && (sortConfig.key === (header.sortField || header.title))) || (header.sortField && currentSortField === header.sortField)) ? 
                      (sortConfig?.direction === 'asc' ? ' ▴' : ' ▾') : ' ↕'
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item, itemIndex) => (
              <TableRow 
                key={itemIndex} 
                className={classNames(
                  !customRowClass && 'even:bg-[#E9EBF5] odd:bg-white',
                  customRowClass && !customRowClass(item) && 'even:bg-[#E9EBF5] odd:bg-white',
                  customRowClass ? customRowClass(item) : '',
                )}
              >
                {renderRow(item, itemIndex)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {((apiPagination && totalCount && totalCount > 0) || (!apiPagination && filteredData.length > 0)) && !disablePagination && (
        <TablePagination
          key={`p-${page}-rpp-${rowsPerPage}-c-${apiPagination ? (totalCount || 0) : filteredData.length}`}
          component="div"
          count={apiPagination ? (totalCount || 0) : filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelDisplayedRows={() => {
            const count = apiPagination ? (totalCount || 0) : filteredData.length;
            const from = count === 0 ? 0 : (page * rowsPerPage) + 1;
            const to = Math.min(count, (page + 1) * rowsPerPage);
            return `${from}-${to} sur ${count}`;
          }}
          labelRowsPerPage="Lignes par page"
        />
      )}
    </div>
  );
};

export default MUITable;
